package com.trevorwiebe.timesheet.loads.presentation.admin

import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.scrollBy
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.layout.boundsInRoot
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.max
import com.trevorwiebe.timesheet.calendar.domain.vibrateTick
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.presentation.TopBar
import com.trevorwiebe.timesheet.core.presentation.common.CollapsingHeaderLayout
import com.trevorwiebe.timesheet.core.presentation.common.botPadding
import com.trevorwiebe.timesheet.core.presentation.common.isAtLeastExpanded
import com.trevorwiebe.timesheet.loads.domain.model.Load
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate

internal val BOARD_BOTTOM_CLEARANCE = 80.dp
private val MIN_COLUMN_WIDTH = 350.dp
// The invisible strip at each side of the board that advances the columns while a drag hovers in it.
private val EDGE_ZONE_WIDTH = 72.dp
private const val UNSCHEDULED_ZONE_KEY = "unscheduled"

@Composable
fun AdminLoadsScreen(
    state: LoadsState,
    onEvent: (LoadsEvent) -> Unit,
    onNavigateToCompleted: () -> Unit,
) {
    val bottomPadding = botPadding()

    Scaffold(
        topBar = {
            TopBar(
                title = "Loads",
                actions = {
                    if (state.isFiltered) {
                        TextButton(onClick = { onEvent(LoadsEvent.OnClearFilters) }) {
                            Text("Clear Filters", color = MaterialTheme.colorScheme.tertiary)
                        }
                    }
                    IconButton(onClick = onNavigateToCompleted) {
                        Icon(
                            Icons.Default.CheckCircle,
                            contentDescription = "Completed loads",
                            tint = MaterialTheme.colorScheme.tertiary,
                            modifier = Modifier.size(36.dp),
                        )
                    }
                }
            )
        }
    ) { paddingValues ->
        // The board uses the full window width instead of the centered,
        // width-capped ResponsiveContent used elsewhere. Once it's wide enough
        // to show every column at once, add a little breathing room on the sides.
        val horizontalPad = if (isAtLeastExpanded()) 24.dp else 0.dp
        val dragState = remember { LoadDragState() }
        var boardBounds by remember { mutableStateOf<Rect?>(null) }
        BoxWithConstraints(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .padding(horizontal = horizontalPad)
                .onGloballyPositioned { boardBounds = it.boundsInRoot() }
        ) {
            when {
                state.isLoading -> Box(modifier = Modifier.fillMaxSize()) {
                    CircularProgressIndicator(
                        modifier = Modifier.align(Alignment.Center),
                        color = MaterialTheme.colorScheme.tertiary
                    )
                }

                else -> {
                    // Tab 0 = Unscheduled; tabs 1..n = the rolling dispatch dates.
                    val dates = state.visibleDates
                    val tabTitles = listOf("Unsch.") + dates.map { Util.toShortDayOfWeekDay(it) }
                    val isExpanded = isAtLeastExpanded()
                    val pagerState = rememberPagerState(pageCount = { tabTitles.size })
                    val scope = rememberCoroutineScope()
                    val columnWidth = max(maxWidth / tabTitles.size, MIN_COLUMN_WIDTH)
                    val columnScrollState = rememberScrollState()
                    // The employee filter only applies to the date (dispatch) tabs, not Unscheduled.
                    val showEmployeeFilter = isExpanded || pagerState.currentPage != 0

                    val edgePx = with(LocalDensity.current) { EDGE_ZONE_WIDTH.toPx() }
                    if (isExpanded) {
                        // Wide layout: pan the column row continuously while a drag hovers an edge.
                        LaunchedEffect(dragState.isDragging) {
                            while (dragState.isDragging) {
                                val bounds = boardBounds
                                if (bounds != null) {
                                    val dir = horizontalEdgeDirection(dragState.pointer, bounds, edgePx)
                                    if (dir != 0) columnScrollState.scrollBy(dir * 14f)
                                }
                                delay(16)
                            }
                        }
                    } else {
                        // Pager: hovering a drag at a screen edge briefly flips to the adjacent tab,
                        // repeating while held so one gesture can cross several dates.
                        LaunchedEffect(dragState.isDragging) {
                            fun edgeDir(): Int =
                                boardBounds?.let { horizontalEdgeDirection(dragState.pointer, it, edgePx) } ?: 0
                            while (dragState.isDragging) {
                                val dir = edgeDir()
                                val target = pagerState.currentPage + dir
                                if (dir == 0 || target !in tabTitles.indices) {
                                    delay(50)
                                    continue
                                }
                                // Dwell: stay on the same edge for ~200ms before the flip fires.
                                var held = true
                                repeat(4) {
                                    if (held) {
                                        delay(50)
                                        if (!dragState.isDragging || edgeDir() != dir) held = false
                                    }
                                }
                                if (held && dragState.isDragging) {
                                    vibrateTick()
                                    pagerState.animateScrollToPage(target)
                                }
                            }
                        }
                    }
                    CollapsingHeaderLayout(
                        header = {
                            Column {
                                LoadsFilterBar(
                                    state = state,
                                    showEmployeeFilter = showEmployeeFilter,
                                    onEvent = onEvent,
                                )
                            }
                        },
                        pinnedHeader = {
                            Column {
                                if (isExpanded) {
                                    StageColumnHeaders(
                                        titles = tabTitles,
                                        columnWidth = columnWidth,
                                        scrollState = columnScrollState,
                                    )
                                } else {
                                    StageTabs(
                                        titles = tabTitles,
                                        selected = pagerState.currentPage,
                                        onSelect = { page ->
                                            scope.launch {
                                                pagerState.animateScrollToPage(page)
                                            }
                                        },
                                    )
                                }
                                HorizontalDivider()
                            }
                        },
                        fab = {
                            FloatingActionButton(
                                onClick = { onEvent(LoadsEvent.OnShowAddDialog) },
                                modifier = Modifier
                                    .padding(end = 16.dp)
                                    .offset(y = -bottomPadding),
                                containerColor = MaterialTheme.colorScheme.tertiary,
                                contentColor = MaterialTheme.colorScheme.onTertiary
                            ) {
                                Icon(Icons.Default.Add, contentDescription = "Add load")
                            }
                        },
                    ) { contentPadding ->
                        if (isExpanded) {
                            Row(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .horizontalScroll(columnScrollState)
                            ) {
                                tabTitles.indices.forEach { page ->
                                    StageContent(
                                        page = page,
                                        dates = dates,
                                        state = state,
                                        dragState = dragState,
                                        bottomPadding = bottomPadding,
                                        contentPadding = contentPadding,
                                        onEvent = onEvent,
                                        modifier = Modifier.width(columnWidth).fillMaxHeight(),
                                    )
                                }
                            }
                        } else {
                            HorizontalPager(
                                state = pagerState,
                                modifier = Modifier.fillMaxSize(),
                                // Keep every page alive during a drag: the gesture's source card
                                // must stay composed while its page flips off-screen.
                                beyondViewportPageCount = if (dragState.isDragging) tabTitles.size else 0,
                            ) { page ->
                                StageContent(
                                    page = page,
                                    dates = dates,
                                    state = state,
                                    dragState = dragState,
                                    bottomPadding = bottomPadding,
                                    contentPadding = contentPadding,
                                    onEvent = onEvent,
                                    modifier = Modifier.fillMaxSize(),
                                )
                            }
                        }
                    }
                }
            }

            // The floating copy of the dragged card, over everything else on the board.
            LoadDragGhost(dragState, state.locations)
        }
    }

    if (state.showAddDialog) {
        AddLoadDialog(state = state, onEvent = onEvent)
    }
}

/** Page 0 = the Unscheduled list; pages 1..n = the dispatch board for [dates]`[page - 1]`. */
@Composable
private fun StageContent(
    page: Int,
    dates: List<LocalDate>,
    state: LoadsState,
    dragState: LoadDragState,
    bottomPadding: Dp,
    contentPadding: PaddingValues,
    onEvent: (LoadsEvent) -> Unit,
    modifier: Modifier = Modifier,
) {
    BoardDragLiftDelay {
        if (page == 0) {
            UnscheduledList(
                loads = state.unscheduledLoads,
                state = state,
                dragState = dragState,
                bottomPadding = bottomPadding,
                contentPadding = contentPadding,
                onEvent = onEvent,
                modifier = modifier,
            )
        } else {
            DispatchBoard(
                state = state,
                date = dates[page - 1],
                dragState = dragState,
                bottomPadding = bottomPadding,
                contentPadding = contentPadding,
                onEvent = onEvent,
                modifier = modifier,
            )
        }
    }
}

@Composable
private fun StageColumnHeaders(
    titles: List<String>,
    columnWidth: Dp,
    scrollState: ScrollState,
) {
    Row(modifier = Modifier.fillMaxWidth().horizontalScroll(scrollState)) {
        titles.forEach { title ->
            Text(
                text = title,
                style = MaterialTheme.typography.titleSmall,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.tertiary,
                modifier = Modifier.width(columnWidth).padding(horizontal = 12.dp, vertical = 10.dp),
            )
        }
    }
}

@Composable
private fun StageTabs(
    titles: List<String>,
    selected: Int,
    onSelect: (Int) -> Unit,
) {
    val listState = rememberLazyListState()
    LaunchedEffect(selected) {
        val info = listState.layoutInfo
        val item = info.visibleItemsInfo.find { it.index == selected }
        val fullyVisible = item != null &&
            item.offset >= info.viewportStartOffset &&
            item.offset + item.size <= info.viewportEndOffset
        if (!fullyVisible) listState.animateScrollToItem(selected)
    }
    LazyRow(state = listState, modifier = Modifier.fillMaxWidth()) {
        itemsIndexed(titles) { index, title ->
            val isSelected = index == selected
            val accent =
                if (isSelected) MaterialTheme.colorScheme.tertiary
                else MaterialTheme.colorScheme.onSurfaceVariant
            Column(
                modifier = Modifier
                    .clickable { onSelect(index) }
                    .padding(top = 10.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Text(
                    text = title,
                    style = MaterialTheme.typography.labelLarge,
                    fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Normal,
                    color = accent,
                    maxLines = 1,
                    modifier = Modifier.padding(horizontal = 16.dp),
                )
                Spacer(Modifier.height(6.dp))
                Box(
                    Modifier.fillMaxWidth().height(2.dp)
                        .background(if (isSelected) accent else Color.Transparent)
                )
            }
        }
    }
}

/**
 * The Unscheduled backlog: its cards drag out to any date board, and the whole list (empty state
 * included) is a drop target that unschedules whatever lands on it.
 */
@Composable
private fun UnscheduledList(
    loads: List<Load>,
    state: LoadsState,
    dragState: LoadDragState,
    bottomPadding: Dp,
    contentPadding: PaddingValues,
    onEvent: (LoadsEvent) -> Unit,
    modifier: Modifier = Modifier,
) {
    val isHovered = dragState.hoveredZoneKey == UNSCHEDULED_ZONE_KEY
    Box(
        modifier = modifier
            .fillMaxSize()
            .loadDropTarget(dragState, UNSCHEDULED_ZONE_KEY, date = null, employeeId = "")
            .then(
                if (isHovered) {
                    Modifier.border(2.dp, MaterialTheme.colorScheme.tertiary, RoundedCornerShape(16.dp))
                } else Modifier
            )
    ) {
        if (loads.isEmpty()) {
            Box(modifier = Modifier.fillMaxSize().padding(contentPadding)) {
                Text(
                    modifier = Modifier.align(Alignment.Center).padding(16.dp),
                    text = "No unscheduled loads.",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    textAlign = TextAlign.Center,
                )
            }
        } else {
            LazyColumn(
                modifier = Modifier.fillMaxSize(),
                contentPadding = contentPadding,
            ) {
                items(loads, key = { it.id }) { load ->
                    val isLifted = dragState.draggedLoad?.id == load.id
                    LoadCard(
                        load = load,
                        locations = state.locations,
                        onClick = { onEvent(LoadsEvent.OnEditLoad(load)) },
                        modifier = Modifier
                            .graphicsLayer { alpha = if (isLifted) 0.35f else 1f }
                            .loadDragSource(dragState, load) { dropped, zone ->
                                onEvent(LoadsEvent.OnMoveLoad(dropped, zone.date, zone.employeeId))
                            },
                    )
                }
                item { Spacer(Modifier.height(bottomPadding + BOARD_BOTTOM_CLEARANCE)) }
            }
        }
    }
}
